import { BaseStorageKeys, type StorageKeyDescriptor } from '@amzn/gsce-shared-react-lib';

/**
 * Gravion v2 localStorage key registry.
 *
 * All client-side persisted state goes through here so keys are:
 * - Namespaced with 'gravion' prefix — no collision with other apps
 * - Versioned — bump the version number when the stored format changes,
 *   stale keys from the previous version are pruned automatically on mount
 * - Discoverable — one place to find every key the app uses
 *
 * Usage (static key):
 *   const [mode, setMode] = useLocalStorage(GravionStorageKeys.themeMode, 'light');
 *
 * Usage (dynamic key scoped per user+BU):
 *   const key = GravionStorageKeys.preferredLocation(alias, businessUnitId);
 *   if (key) {
 *     const [loc, setLoc, clearLoc] = useLocalStorage(key, undefined);
 *   }
 *
 * Note: useTimezonePreference intentionally does NOT use this class.
 * The timezone key (TIME_ZONE) has a legacy contract with the desktop
 * app which reads it via JSON.parse. useLocalStorage would double-encode
 * the value; the manual implementation is kept to preserve compatibility.
 */
export class GravionStorageKeys extends BaseStorageKeys {
  static override readonly prefix = 'gravion';

  /** Light/dark theme preference. Value: ThemeMode ('light' | 'dark'). */
  static readonly themeMode = GravionStorageKeys.key('theme-mode');

  /** Feature flag local overrides (non-prod only). */
  static readonly featureFlags = GravionStorageKeys.key('feature-flags');

  /**
   * Preferred location for a specific (alias, businessUnitId) pair.
   * Returns a descriptor whose resolved key includes both runtime values,
   * or null when either input is missing (no key can be built).
   */
  static preferredLocation(
    alias: string,
    businessUnitId: string | undefined,
  ): StorageKeyDescriptor | null {
    if (!alias || !businessUnitId) return null;
    const name = `preferred-location:${alias}:${businessUnitId}`;
    return {
      key: name,
      prefix: GravionStorageKeys.prefix,
      version: 1,
      resolved: `${GravionStorageKeys.prefix}:${name}:v1`,
    };
  }
}
